import { type WeightData } from "../models/WeightData";
import { DEFAULT_UNIT_SYMBOL, WEIGHT_DECIMAL_DIGITS } from "./constants";
import { NumberFormatter as NF } from "./NumberFormatter";

export enum WeightUnit {
  g,
  dag,
  kg,
  t,
  lbs,
}

const MULTIPLIERS = {
  [WeightUnit.g]: 1,
  [WeightUnit.dag]: 10,
  [WeightUnit.kg]: 1000,
  [WeightUnit.t]: 1000000,
  [WeightUnit.lbs]: 2200,
};

const unitStr = {
  [WeightUnit.g]: "g",
  [WeightUnit.dag]: "dag",
  [WeightUnit.kg]: "kg",
  [WeightUnit.t]: "t",
  [WeightUnit.lbs]: "lbs",
};

const strUnit = {
  g: WeightUnit.g,
  dag: WeightUnit.dag,
  kg: WeightUnit.kg,
  t: WeightUnit.t,
  lbs: WeightUnit.lbs,
};

export function sum(...weights: WeightData[]) {
  const first = weights.shift();

  if (!first) {
    return zero();
  }

  let accumulator = first.value;

  const firstMulti = MULTIPLIERS[first.symbol];

  for (const weight of weights) {
    accumulator +=
      weight.symbol === first.symbol ? weight.value : internal_convert(weight.value, weight.symbol, first.symbol);
  }

  if (firstMulti < MULTIPLIERS[WeightUnit.kg]) {
    accumulator = internal_convert(accumulator, firstMulti, WeightUnit.kg);
    return {
      value: accumulator,
      symbol: WeightUnit.kg,
    };
  }

  return {
    value: accumulator,
    symbol: first.symbol,
  };
}

export function multiply(weight: WeightData, multiplier: number): WeightData {
  return {
    value: weight.value * multiplier,
    symbol: weight.symbol,
  };
}

const ZERO_WEIGHT = {
  value: 0,
  symbol: WeightUnit.kg,
};
export function zero(): WeightData {
  return ZERO_WEIGHT;
}

export function text(weight: WeightData, targetUnit?: WeightUnit): string {
  if (!targetUnit) {
    targetUnit = findPreferredWeightUnit(weight);
  }

  const formatter = targetUnit === WeightUnit.g ? NF.getWeightFormatterG() : NF.getWeightFormatterKg();

  const convertedValue = internal_convert(weight.value, weight.symbol, targetUnit);
  const val = formatter.format(convertedValue);
  const symbol = unitStr[targetUnit];
  return `${val}\xa0${symbol}`;
}

function findPreferredWeightUnit(weight: WeightData) {
  const weightInGram = internal_convert(weight.value, weight.symbol, WeightUnit.g);
  return weightInGram <= 100 ? WeightUnit.g : WeightUnit.kg;
}

export function fromString(strSymbol: string = DEFAULT_UNIT_SYMBOL) {
  const formattedSymbol = strSymbol.trim().toLowerCase();
  // @ts-expect-error
  const symbol = strUnit[formattedSymbol];
  if (symbol === undefined) {
    return WeightUnit.kg;
  }
  return symbol;
}

export function getWeightObject(value?: number | null, unit?: string | null): WeightData {
  return {
    value: value || 0,
    symbol: fromString(unit || undefined),
  };
}

/**
 * Rounds the weight to the prevision used for displaying
 * @param {WeightData} weight
 * @returns {WeightData} rounded weight
 */
export function roundWeight(weight: WeightData): WeightData {
  return {
    ...weight,
    value: +weight.value.toFixed(WEIGHT_DECIMAL_DIGITS),
  };
}

function internal_convert(value: number, unit: WeightUnit, newUnit: WeightUnit) {
  return (value * MULTIPLIERS[unit]) / MULTIPLIERS[newUnit];
}

export function convert(value: WeightData, newUnit: WeightUnit): WeightData {
  return {
    value: internal_convert(value.value, value.symbol, newUnit),
    symbol: newUnit,
  };
}

export function getPreferredUnitSymbol(weightData: WeightData) {
  const weightUnit = findPreferredWeightUnit(weightData);
  return unitStr[weightUnit];
}
